import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaShieldAlt, FaRocket } from 'react-icons/fa';
import { MdEmail, MdSecurity } from 'react-icons/md';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  
  const { login } = useAuth();
  const navigate = useNavigate();

  // Floating animation for background elements
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const x = (clientX / window.innerWidth) * 100;
      const y = (clientY / window.innerHeight) * 100;
      
      document.documentElement.style.setProperty('--mouse-x', `${x}%`);
      document.documentElement.style.setProperty('--mouse-y', `${y}%`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleInputFocus = (field) => {
    setIsFocused(prev => ({ ...prev, [field]: true }));
  };

  const handleInputBlur = (field) => {
    setIsFocused(prev => ({ ...prev, [field]: false }));
  };

  return (
    <div className="modern-login-container">
      {/* Animated Background Elements */}
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
        <div className="shape shape-5"></div>
      </div>

      {/* Gradient Overlay */}
      <div className="gradient-overlay"></div>

      {/* Main Login Card */}
      <div className="modern-login-card">
        {/* Header Section */}
        <div className="login-header">
          <div className="logo-container">
            <div className="logo-icon">
              <FaShieldAlt />
            </div>
          </div>
          <h1 className="login-title">
            <span className="title-main">Publication</span>
            <span className="title-sub">Management System</span>
          </h1>
          <p className="login-subtitle">Welcome back! Please sign in to continue</p>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="modern-login-form">
          {error && (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <div className="error-message">{error}</div>
            </div>
          )}
          
          {/* Email Input */}
          <div className={`input-container ${isFocused.email ? 'focused' : ''}`}>
            <div className="input-icon-wrapper">
              <MdEmail className="input-icon" />
            </div>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => handleInputFocus('email')}
              onBlur={() => handleInputBlur('email')}
              required
              className="modern-input"
            />
            <div className="input-border"></div>
          </div>
          
          {/* Password Input */}
          <div className={`input-container ${isFocused.password ? 'focused' : ''}`}>
            <div className="input-icon-wrapper">
              <MdSecurity className="input-icon" />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => handleInputFocus('password')}
              onBlur={() => handleInputBlur('password')}
              required
              className="modern-input"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FaEyeSlash className="eye-icon" /> : <FaEye className="eye-icon" />}
            </button>
            <div className="input-border"></div>
          </div>
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="modern-login-button"
          >
            <span className="button-content">
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <FaRocket className="button-icon" />
                  <span>Sign In</span>
                </>
              )}
            </span>
            <div className="button-glow"></div>
          </button>
        </form>



        {/* Decorative Elements */}
        <div className="card-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-line"></div>
        </div>
      </div>

      {/* Background Particles */}
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{
            '--delay': `${Math.random() * 3}s`,
            '--duration': `${2 + Math.random() * 3}s`
          }}></div>
        ))}
      </div>
    </div>
  );
};

export default Login; 